import * as React from 'react'
import {
  useCurrentIngestSession,
  usePlantSessionPaint,
  type BranchIngestJobSummary,
  type PlantPaintSelection
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import {
  allChangesCountFromMarks,
  jobLevelCountFromMarks,
  sectionCountsFromMarks
} from './plant-changes-bar-counts'

/**
 * Slim control surface above the Plant tab strip while session paint is active.
 * Owns no filter state — drives `usePlantSessionPaint().filter` only.
 */
export function PlantChangesBar(): React.ReactElement | null {
  const paint = usePlantSessionPaint()
  const { outcome } = useCurrentIngestSession()
  const counts = React.useMemo(
    () => sectionCountsFromMarks(paint.branchMarkById, paint.branchRelationshipMarkById),
    [paint.branchMarkById, paint.branchRelationshipMarkById]
  )
  const jobLevelCount = React.useMemo(
    () => jobLevelCountFromMarks(paint.branchMarkById, paint.branchRelationshipMarkById),
    [paint.branchMarkById, paint.branchRelationshipMarkById]
  )
  const allChangesCount = React.useMemo(
    () => allChangesCountFromMarks(paint.branchMarkById, paint.branchRelationshipMarkById),
    [paint.branchMarkById, paint.branchRelationshipMarkById]
  )
  const ingestJobs = React.useMemo(
    () => ingestJobsForPaintDropdown(paint.jobsSummary),
    [paint.jobsSummary]
  )
  if (!paint.active) return null

  if (!paint.filter.enabled) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-line bg-bg-elev/60 px-2.5 py-1.5">
        <span className="min-w-0 flex-1 truncate text-12 text-ink-3">Session changes hidden</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-11"
          onClick={() => paint.filter.setEnabled(true)}
        >
          Show changes
        </Button>
      </div>
    )
  }

  const agentOnly = paint.agentOnly
  const fileName = agentOnly ? 'Agent edits' : paint.session?.fileName?.trim() || 'Ingest session'
  const stateLabel = agentOnly
    ? 'active'
    : outcome === 'failed'
      ? 'failed'
      : outcome === 'pending'
        ? 'reviewing'
        : 'active'
  const showJobLevel = !agentOnly && paint.sections.length > 0 && jobLevelCount > 0
  const showAgentEdits = paint.hasAgentEdits
  const selectValue =
    paint.filter.selection.kind === 'jobLevel' && !showJobLevel
      ? 'all'
      : selectionToValue(paint.filter.selection)

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-sess/30 bg-sess-bg/50 px-2.5 py-1.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="size-1.5 shrink-0 rounded-full bg-sess" aria-hidden />
        <span className="min-w-0 truncate text-12 font-medium text-ink" title={fileName}>
          {fileName}
        </span>
        <Badge
          variant="outline"
          className="shrink-0 border-sess/40 bg-sess-bg px-1.5 py-0 text-10 font-normal text-sess"
        >
          {stateLabel}
        </Badge>
      </div>

      <PaintLegend />

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <Select
          value={selectValue}
          onValueChange={(value) => paint.filter.setSelection(valueToSelection(value))}
        >
          <SelectTrigger
            size="sm"
            aria-label="Change filter"
            className="h-6 min-w-40 max-w-64 gap-1 border-sess/40 bg-card px-2 text-11 text-ink shadow-none [&_svg]:size-3.5"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            position="popper"
            align="start"
            className="min-w-[var(--radix-select-trigger-width)] border-line bg-card text-ink shadow-md"
          >
            <SelectItem value="all" className="text-11">
              All changes
              <span className="font-mono text-10 text-ink-4 tabular-nums">{allChangesCount}</span>
            </SelectItem>
            {paint.sections.map((section) => (
              <SelectItem key={section.id} value={`section:${section.id}`} className="text-11">
                <span className="truncate">{section.name}</span>
                <span className="font-mono text-10 text-ink-4 tabular-nums">
                  {counts.get(section.id) ?? 0}
                </span>
              </SelectItem>
            ))}
            {showJobLevel ? (
              <SelectItem value="jobLevel" className="text-11">
                Job-level
                <span className="font-mono text-10 text-ink-4 tabular-nums">{jobLevelCount}</span>
              </SelectItem>
            ) : null}
            {ingestJobs.length > 0 ? (
              <SelectGroup>
                <SelectLabel className="text-10 text-ink-4">Jobs</SelectLabel>
                {ingestJobs.map((job) => (
                  <SelectItem key={job.jobId} value={`sourceJob:${job.jobId}`} className="text-11">
                    <span className="truncate">{sourceJobLabel(job)}</span>
                    <span className="font-mono text-10 text-ink-4 tabular-nums">
                      {job.changeCount ?? job.rowCount}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : null}
            {showAgentEdits ? (
              <SelectItem value="agent" className="text-11">
                Agent edits
                <span className="font-mono text-10 text-ink-4 tabular-nums">
                  {paint.agentChangesCount}
                </span>
              </SelectItem>
            ) : null}
          </SelectContent>
        </Select>
        {paint.inactiveSectionChangeCount > 0 ? (
          <Badge
            variant="outline"
            className="h-6 border-st-warn/40 bg-st-warn-bg px-2 text-10 font-normal text-st-warn"
            title="Changes reference a skipped or otherwise inactive section and remain visible under All changes."
          >
            {paint.inactiveSectionChangeCount} inactive
          </Badge>
        ) : null}
        {paint.truncated ? (
          <Badge
            variant="outline"
            className="h-6 border-line bg-bg-elev px-2 text-10 font-normal text-ink-3"
            title="Marks show the first batch of changes; rollup counts stay exact for the full job."
          >
            partial marks
          </Badge>
        ) : null}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto h-6 shrink-0 px-2 text-11 text-ink-3 hover:text-ink"
        onClick={() => paint.filter.setEnabled(false)}
      >
        Hide changes
      </Button>
    </div>
  )
}

/** Ingest-domain jobs with a non-zero net change for the Plant Jobs dropdown. */
export function ingestJobsForPaintDropdown(
  jobs: readonly BranchIngestJobSummary[]
): BranchIngestJobSummary[] {
  return jobs.filter(
    (job) =>
      job.domain !== 'agent' && job.kind !== 'agent_edit' && (job.changeCount ?? job.rowCount) > 0
  )
}

export function sourceJobLabel(job: BranchIngestJobSummary): string {
  const name = job.fileName?.trim() || job.title?.trim() || job.jobId
  const type = job.fileType?.trim() || job.kind
  return `${name} · ${type}`
}

export function selectionToValue(selection: PlantPaintSelection): string {
  if (selection.kind === 'section') return `section:${selection.sectionId}`
  if (selection.kind === 'sourceJob') return `sourceJob:${selection.jobId}`
  return selection.kind
}

export function valueToSelection(value: string): PlantPaintSelection {
  if (value === 'jobLevel') return { kind: 'jobLevel' }
  if (value === 'agent') return { kind: 'agent' }
  if (value.startsWith('section:')) {
    return { kind: 'section', sectionId: value.slice('section:'.length) }
  }
  if (value.startsWith('sourceJob:')) {
    return { kind: 'sourceJob', jobId: value.slice('sourceJob:'.length) }
  }
  return { kind: 'all' }
}

function PaintLegend(): React.ReactElement {
  return (
    <div
      className="flex shrink-0 items-center gap-2 border-l border-line pl-3 text-10 text-ink-3"
      aria-label="Change legend"
    >
      <span className="inline-flex items-center gap-1">
        <span className="size-2.5 rounded-sm border border-st-run/40 bg-st-run-bg" aria-hidden />
        new
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="size-1.5 rounded-full bg-st-warn" aria-hidden />
        updated
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="text-10 font-medium text-st-down line-through decoration-st-down"
          aria-hidden
        >
          Aa
        </span>
        deleted
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="size-2.5 rounded-sm border border-line bg-bg-sunk opacity-60"
          aria-hidden
        />
        other sections
      </span>
    </div>
  )
}
